if (!customElements.get('cart-vouchers')) {
  /**
   * The `CartVouchers` class is a custom web component that manages the display, calculation,
   * and redemption of vouchers in a shopping cart. It interacts with shipping and cart details
   * stored in the session and provides hooks for voucher calculations and UI updates.
   */
  class CartVouchers extends HTMLElement {
    constructor() {
      super();
    }

    /**
     * Lifecycle method that is called when the component is added to the DOM.
     * Initializes the component by caching required DOM elements, triggering
     * a global event signaling readiness, and invoking the initialization logic
     * if necessary.
     *
     * @return {void} This method does not return a value.
     */
    connectedCallback() {
      this.working = false;
      this.initialized = false;
      this.data = null;
      this.amount = null;
      this.address = null;
      this.vouchersRedeemed = false;
      this.container = this.querySelector('.cart-vouchers--container');
      this.loader = this.querySelector('.loader');
      this.totals = this.querySelector('.cart-vouchers--totals');
      this.cartShippingDetails = document.querySelector('cart-shipping-details');
      this.drawer = this.dataset.drawer === 'true';

      this.hide();
      this.showLoader();

      document.dispatchEvent(new CustomEvent('cart:vouchers-ready'));

      if (!this.initialized && this.container?.querySelector('.text')) {
        this.init();
        this.initialized = true;
      }
    }

    /**
     * Initializes the necessary settings for the component by retrieving saved data from local storage,
     * calculating applicable vouchers, and setting up event listeners.
     *
     * @return {void} This method does not return any value.
     */
    init() {
      let savedAddress = localStorage.getItem('shippingAddress');
      if (savedAddress) this.address = JSON.parse(savedAddress);

      this.handleCalculateVouchers();
      this.setupEventListeners();
    }

    /**
     * Sets up event listeners for various cart-related actions and UI elements.
     * This method initializes listeners for cart updates, refresh actions,
     * shipping method changes, item additions, and user interactions with
     * specific UI elements such as the checkout button and voucher redemption.
     *
     * @return {void} This method does not return a value.
     */
    setupEventListeners() {

      document.addEventListener('cart:shipping-method-reset', () => {
        this.resetVoucherState();
      });

      document.addEventListener('cart:refresh', (event) => {
        this.handleCalculateVouchers();
      });

      document.addEventListener('cart:shipping-method-changed', (event) => {
        let savedAddress = localStorage.getItem('shippingAddress');
        if (savedAddress) this.address = JSON.parse(savedAddress);
        this.handleCalculateVouchers();
      });

      if (this.querySelector('button.checkout-button')) {
        this.querySelector('button.checkout-button.loading').addEventListener('click', (e) => {
          e.preventDefault();
        });
      }

      if (this.querySelector('.template-cart .cart-vouchers--redeem')) {
        this.querySelector('.template-cart .cart-vouchers--redeem').addEventListener('click', (e) => {
          e.preventDefault();
          let btn = e.target;

          if (btn.closest('.cart-vouchers--redeem')) {
            btn = btn.closest('.cart-vouchers--redeem');
          }

          if (btn.classList.contains('loading')) return;

          if (btn.classList.contains('redeemed')) {
            this.handleCancelRedeem();
          } else {
            this.handleRedeem(btn);
          }
        });
      }

    }

    /**
     * Displays the loader by removing the 'hidden' class from the loader element.
     *
     * @return {void} This method does not return a value.
     */
    showLoader() {
      this.loader?.classList.remove('hidden');
    }

    /**
     * Hides the loader by removing the 'hidden' class from the loader element.
     * @return {void} Does not return a value.
     */
    hideLoader() {
      this.loader?.classList.add('hidden');
    }

    /**
     * Displays the element by removing the 'hidden' class from its class list.
     * @return {void} Does not return a value.
     */
    show() {
      this.container?.classList.remove('hidden');
    }

    /**
     * Hides the element by adding the 'hidden' class to its class list.
     * @return {void} This method does not return a value.
     */
    hide() {
      this.container?.classList.add('hidden');
    }

    /**
     * Fetches the current cart data from the server and updates the global `window.cart` variable.
     *
     * @return {Promise<Object>} A promise that resolves to the cart data as an object or rejects with an error.
     */
    getCart() {
      return new Promise((resolve, reject) => {
        fetch('/cart.js')
          .then(res => res.json())
          .then(data => {
            window.cart = data;
            resolve(data);
          })
          .catch(error => reject(error));
      });
    }

    /**
     * Handles the calculation of vouchers for the shopping cart.
     * This includes fetching cart data, calculating available vouchers, updating
     * the voucher widget, and managing the UI state during the process. If any
     * errors occur, it handles error display appropriately.
     *
     * @return {void} Does not return any value.
     */
    handleCalculateVouchers() {
      if (!window.customer_id || !this.address || !this.address.shipping_method || !this.address.shipping_method.title) {
        this.resetVoucherState();
        return;
      }

      if (!this.working) {
        this.working = true;
        this.hide();
        this.showLoader();
        this.getCart().then(() => {
          this.calculateVouchers().then(response => {
            this.data = response;
            this.updateVouchersWidgetAmount(response);
            this.updateVouchersWidgetType(response);
            this.updateTotals();

            if (this.vouchersRedeemed) {
              this.handleRedeem(this.querySelector('.cart-vouchers--redeem'));
            }
            this.hideLoader();
            this.show();
          }).catch(error => {
            this.hide();
            this.hideLoader();
            this.cartShippingDetails?.showErrorPopup(error);
          }).finally(() => {
            this.working = false;
          });
        }).catch(error => {
          this.hide();
          this.hideLoader();
          this.cartShippingDetails?.showErrorPopup(error);
        });

      } else {
        this.hide();
        this.hideLoader();
      }
    }

    resetVoucherState() {
      this.vouchersRedeemed = false;
      this.data = null;
      this.amount = null;
      this.hide();
      this.hideLoader();
      this.updateCheckoutButton();
      this.updateRedeemButton();
      this.updateVouchersWidgetText();
    }

    /**
     * Calculates applicable vouchers for the current cart and shipping details.
     * Sends a request to the server with cart items and shipping address data to determine voucher eligibility.
     *
     * @return {Promise<Object>} A promise that resolves with the calculated voucher data or rejects with an error message.
     */
    calculateVouchers() {
      return new Promise((resolve, reject) => {
        if (!this.address) {
          reject('No shipping address found');
          return;
        }
        let items = this.prepCartItems();

        let address = {};

        address = {
          address1: this.address['shipping_address[address]'],
          city: this.address['shipping_address[city]'],
          zip: this.address['shipping_address[zip]'],
          state_code: this.address['shipping_address[state_code]'],
          country_code: this.address['shipping_address[country_code]']
        }


        let data = {
          customer_id: window.customer_id ?? '',
          items: items,
          shipping_address: address,
          shipping_method: this.address?.shipping_method ?? {}
        };

        fetch('/apps/portal/vouchers/calculate', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify(data)
        })
          .then(res => res.json())
          .then(data => {
            if (data.amount < 1 && data.hasOwnProperty('message')) {
              reject(data.message);
              return;
            }
            resolve(data);
          })
          .catch(error => {
            reject(error)
          });
      });
    }

    /**
     * Handles the redeem action for vouchers, updates buttons, and adjusts totals based on the response.
     *
     * @param {HTMLElement} btn - The button element triggering the redeem action.
     * @return {void} This method does not return a value.
     */
    handleRedeem(btn) {
      this.toggleCheckoutButton(true);
      btn.classList.add('loading');

      this.redeemVouchers().then(response => {
        this.data = response;
        this.vouchersRedeemed = true;
        this.updateCheckoutButton();
        this.toggleCheckoutButton(false);
        this.updateRedeemButton();
        this.updateTotals();
        this.updateVouchersWidgetText();
        btn.classList.remove('loading');
      }).catch(error => {
        this.handleCancelRedeem();
        this.cartShippingDetails?.showErrorPopup(error);
        btn.classList.remove('loading');
      })
    }

    /**
     * Handles the cancellation of the redeem action, resetting related states and updating the UI accordingly.
     *
     * @return {void} Does not return any value.
     */
    handleCancelRedeem() {
      this.vouchersRedeemed = false;
      this.updateCheckoutButton();
      this.toggleCheckoutButton(false);
      this.updateTotals();
      this.updateVouchersWidgetText();
      this.updateRedeemButton();
    }

    /**
     * Redeems vouchers for the items in the cart and processes them with the provided shipping information.
     *
     * This method collects cart item details and shipping address information, then sends a POST request
     * to a server endpoint to redeem vouchers. It resolves to the response data from the server or rejects
     * in case the shipping address is not found.
     *
     * @return {Promise<Object>} A Promise that resolves to the server response if voucher redemption is successful,
     * or rejects with an error message if no shipping address is found or there is an issue with the request.
     */
    redeemVouchers() {
      return new Promise((resolve, reject) => {

        if (!this.address) {
          reject('No shipping address found');
          return;
        }

        let items = this.prepCartItems();

        let address = {};

        address = {
          address1: this.address['shipping_address[address]'],
          city: this.address['shipping_address[city]'],
          zip: this.address['shipping_address[zip]'],
          state_code: this.address['shipping_address[state_code]'],
          country_code: this.address['shipping_address[country_code]']
        }


        let data = {
          customer_id: window.customer_id ?? '',
          items: items,
          shipping_address: address,
          shipping_method: this.address?.shipping_method ?? {}
        };

        fetch('/apps/portal/vouchers/redeem', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify(data)
        })
          .then(res => res.json())
          .then(data => {
            resolve(data);
          });
      });
    }

    /**
     * Prepares and returns an array of cart items, extracting relevant details from the global `window.cart` object.
     * Each item in the returned array includes properties such as `id`, `quantity`, `variant_id`, `product_id`, `properties`, and a parsed `price`.
     *
     * @return {Array<Object>} An array of cart item objects with extracted and formatted details. If no items are found, returns an empty array.
     */
    prepCartItems() {
      let items = [];
      if (window.cart?.items) {
        window.cart.items.forEach(item => {
          items.push({
            id: item.key,
            quantity: item.quantity,
            variant_id: item.variant_id,
            product_id: item.product_id,
            properties: item.properties,
            price: parseInt(item.price)
          })
        })
      }
      return items;
    }

    getEligibleVoucherAmount(data = this.data) {
      if (!data) return 0;

      let baseAmount = Number(data?.amount ?? 0);
      let shippingPrice = Number(data?.original?.shipping ?? 0);
      let eligibleAmount = baseAmount + shippingPrice;

      let voucherCapCandidates = [
        data?.voucher_amount,
        data?.voucher_total,
        data?.available_amount,
        data?.max_amount,
        data?.redeemed?.grand_total
      ];

      let voucherCap = voucherCapCandidates.find(value => Number.isFinite(Number(value)) && Number(value) > 0);
      if (voucherCap) {
        eligibleAmount = Math.min(eligibleAmount, Number(voucherCap));
      }

      return Math.max(0, Math.round(eligibleAmount));
    }

    /**
     * Updates the various total fields in the user interface, including subtotal, total, voucher applied, shipping, and tax.
     * These updates are based on the current state of the data and redeemed vouchers.
     *
     * @return {void} No return value.
     */
    updateTotals() {
      if (!this.totals) return;

      let subtotalEl = this.totals.querySelector('.subtotal .value');
      let totalEl = this.totals.querySelector('.total .value');
      let voucherAppliedEl = this.totals.querySelector('.voucher-applied');
      let voucherAppliedValEl = voucherAppliedEl.querySelector('.voucher-applied .value');
      let shippingEl = this.totals.querySelector('.shipping .value');
      let taxEl = this.totals.querySelector('.tax .value');

      let source = this.vouchersRedeemed ? 'remaining' : 'original';

      let subtotalPrice = this.data?.original?.subtotal ?? 0.00;
      subtotalPrice = (subtotalPrice / 100).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });

      let shippingPrice = 0;
      let taxPrice = 0;

      // Update the subtotal
      if (subtotalEl) {
        subtotalEl.innerHTML = `$${subtotalPrice}`;
      }

      // Update the applied voucher value
      if (this.amount) {
        if (voucherAppliedValEl) {
          if (this.vouchersRedeemed) {
            let redeemedAmount = Number(this.data?.redeemed?.grand_total ?? 0);
            let voucherAmount = this.amount > 0 ? Math.min(redeemedAmount, this.amount) : redeemedAmount;
            voucherAppliedValEl.innerHTML = `-$${(voucherAmount / 100).toLocaleString('en-US', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2
            }) ?? 0}`;
          } else {
            voucherAppliedValEl.innerHTML = '$0';
          }
        }
        voucherAppliedEl.classList.remove('hidden');
      }

      // Update the shipping price
      if (shippingEl) {
        shippingPrice = this.data?.[source]?.shipping ?? 0;
        let notice = shippingEl.querySelector('.notice');
        let amount = shippingEl.querySelector('.amount');

        notice.classList.add('hidden');
        amount.classList.remove('hidden');

        let formattedShippingPrice = (shippingPrice / 100).toLocaleString('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        });

        let shippingPriceString = shippingPrice > 0 ? `$${formattedShippingPrice}` : 'FREE';
        const shippingMethodTitle = this.address?.shipping_method?.title || '';
        amount.innerHTML = shippingMethodTitle ? `${shippingPriceString} (${shippingMethodTitle})` : `${shippingPriceString}`;
      }

      // Update the tax price
      if (taxEl) {
        let notice = taxEl.querySelector('.notice');
        let amount = taxEl.querySelector('.amount');

        notice.classList.add('hidden');
        amount.classList.remove('hidden');

        taxPrice = this.data?.[source]?.tax ?? 0
        let taxPriceString = (taxPrice / 100).toLocaleString('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        })

        amount.innerHTML = `$${taxPriceString}`;
      }

      // Update the total price
      if (totalEl) {
        let newPrice = this.data?.[source]?.subtotal ?? 0;

        // Manually add shipping and tax costs
        newPrice += shippingPrice;
        newPrice += taxPrice;

        newPrice = (newPrice / 100).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})
        totalEl.innerHTML = `$${newPrice}`;

        // Update the checkout button total
        if (document.querySelector('.checkout-button .wcp-original-cart-total')) {
          document.querySelector('.checkout-button .wcp-original-cart-total').innerHTML = `$${newPrice}`;
        }
      }

      this.updateCheckoutButton();
    }

    /**
     * Toggles the state of the checkout button based on whether a loading process is active.
     *
     * @param {boolean} [loading=false] - Indicates whether the checkout button should be in a loading state.
     *                                    If true, the button is disabled and a loading class is added.
     *                                    If false, the button is enabled and the loading class is removed.
     * @return {void} This method does not return a value.
     */
    toggleCheckoutButton(loading = false) {
      let checkoutBtn = document.querySelector('button.checkout-button');
      if (checkoutBtn) {
        if (loading) {
          checkoutBtn.classList.add('loading');
          checkoutBtn.toggleAttribute('disabled', true);
        } else {
          checkoutBtn.classList.remove('loading');
          checkoutBtn.toggleAttribute('disabled', false);
        }
      }
    }

    /**
     * Updates the visibility and content of the checkout button and vouchers checkout button based on the current voucher redemption state.
     *
     * The method checks for the presence of the primary checkout button and the vouchers checkout button. If both buttons are present:
     * - It adjusts the `href` attribute of the vouchers checkout button based on the invoice URL.
     * - If vouchers have been redeemed:
     *   - Updates the old amount and new amount displayed on the vouchers checkout button with the appropriate values calculated from the original and remaining subtotal, tax, and shipping data.
     *   - Hides the primary checkout button and displays the vouchers checkout button.
     * - If vouchers have not been redeemed:
     *   - Shows the primary checkout button and hides the vouchers checkout button.
     *
     * @return {void} This method does not return a value.
     */
    updateCheckoutButton() {
      let checkoutBtn = document.querySelector('button.checkout-button');
      let vouchersCheckoutBtn = document.querySelector('.vouchers-checkout-button');

      if (checkoutBtn && vouchersCheckoutBtn) {
        vouchersCheckoutBtn.href = this.data?.invoice_url ?? '/checkout';

        if (this.vouchersRedeemed) {
          let oldAmount = vouchersCheckoutBtn.querySelector('.old-amount');
          let newAmount = vouchersCheckoutBtn.querySelector('.new-amount');
          if (oldAmount) {
            let total = this.data?.original?.grand_total ?? 0;
            oldAmount.innerHTML = `$${(total / 100).toLocaleString('en-US', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2
            })}`;
          }
          if (newAmount) {
            let remaining = this.data?.remaining?.grand_total ?? 0;
            newAmount.innerHTML = `$${(remaining / 100).toLocaleString('en-US', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2
            })}`;
          }

          checkoutBtn.classList.add('hidden');
          vouchersCheckoutBtn.classList.remove('hidden');
        } else {
          checkoutBtn.classList.remove('hidden');
          vouchersCheckoutBtn.classList.add('hidden');
        }
      }
    }

    /**
     * Updates the state of the "Redeem" button based on whether vouchers have been redeemed or not.
     * Modifies the button's appearance and displayed value to reflect the redemption status.
     *
     * @return {void} This method does not return a value.
     */
    updateRedeemButton() {
      let target = this.querySelector('.cart-vouchers--redeem');
      if (target) {
        if (this.vouchersRedeemed) {
          let amountEl = target.querySelector('.value');

          target.classList.add('redeemed');
          let redeemedAmount = Number(this.data?.redeemed?.grand_total ?? 0);
          let value = this.amount > 0 ? Math.min(redeemedAmount, this.amount) : redeemedAmount;
          amountEl.innerHTML = `$${(value / 100).toLocaleString('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          })}`;
        } else {
          target.classList.remove('redeemed');
        }
      }
    }

    /**
     * Updates the vouchers widget by updating the displayed voucher amount
     * based on the provided response. If no valid amount is specified or the
     * container is not available, the widget is hidden.
     *
     * @param {Object} response The response object containing voucher data.
     * @param {number} response.amount The amount of the voucher in cents.
     *                                  Must be greater than zero to update the widget.
     * @return {void} Does not return any value.
     */
    updateVouchersWidgetAmount(response) {
      if (!this.container) return;
      let textEl = this.container.querySelector('.text');
      if (response.hasOwnProperty('amount') && response.amount > 0 && textEl?.isConnected) {
        this.amount = this.getEligibleVoucherAmount(response);
        if (textEl.querySelector('.cart-vouchers--amount')) {
          textEl.querySelector('.cart-vouchers--amount').innerHTML = `$${(this.amount / 100).toFixed(2)}`;
        } else {
          textEl.innerHTML = textEl.innerHTML.replace('[AMOUNT]', `<span class="cart-vouchers--amount">$${(this.amount / 100).toFixed(2)}</span>`);
        }
        this.show();
        this.hideLoader();
      } else {
        this.hide();
        this.hideLoader();
      }
    }

    /**
     * Updates the vouchers widget type by modifying the content of the associated container
     * based on the provided response object.
     *
     * @param {Object} response - The response object containing information about voucher types.
     * @param {Array} response.types - An array of objects representing the voucher types.
     * @param {string} [response.types[].type] - The name or description of a voucher type.
     *
     * @return {void} This method does not return a value.
     */
    updateVouchersWidgetType(response) {
      if (!this.container) return;
      let textEl = this.container.querySelector('.text');
      if (response.hasOwnProperty('types') && textEl?.isConnected) {
        let typeText = '';

        if (response.types.length > 0) {
          response.types.forEach(type => {
            if (typeText.length > 0) typeText += ' and ';
            typeText += type.type ?? '';
          })
        }

        if (textEl.querySelector('.cart-vouchers--type')) {
          textEl.querySelector('.cart-vouchers--type').innerHTML = `$${typeText}`;
        } else {
          textEl.innerHTML = textEl.innerHTML.replace('[TYPE]', `<span class="cart-vouchers--type">${typeText}</span>`);
        }
      } else {
        this.hide();
        this.hideLoader();
      }
    }

    /**
     * Updates the visibility of the message and success elements
     * within the vouchers widget based on the redemption status.
     *
     * The method toggles the 'hidden' class for two elements:
     * - `messageEl`: Represents the element displaying the message.
     * - `successEl`: Represents the element displaying the success state.
     *
     * If `vouchersRedeemed` is true, the method hides the message element
     * and displays the success element. Otherwise, it displays the message
     * element and hides the success element. Both elements are identified
     * by their specific selectors within the container.
     *
     * @return {void} This method does not return a value.
     */
    updateVouchersWidgetText() {
      let messageEl = this.container.querySelector('.main-text .message');
      let successEl = this.container.querySelector('.main-text .success');

      if (messageEl && successEl) {
        if (this.vouchersRedeemed) {
          messageEl.classList.add('hidden');
          successEl.classList.remove('hidden');
        } else {
          messageEl.classList.remove('hidden');
          successEl.classList.add('hidden');
        }
      }
    }
  }

  customElements.define('cart-vouchers', CartVouchers);
}


document.addEventListener('cart-drawer:open', (event) => {
  document.querySelector('cart-drawer cart-vouchers').connectedCallback();
});